import { useState, useEffect, useCallback } from 'react'
import { AlertOctagon, AlertTriangle, Info, CheckCircle2, X } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { safeStorage } from '../lib/safeStorage'

// ── AnnouncementBanner ───────────────────────────────────────────────────────
// The public-facing twin of OpsAlertBanner: that one is a loud, admin-only ops
// signal pinned as a fixed overlay; this one is a calmer, everyone-sees-it
// notice, rendered IN FLOW at the top of the app (pushes content down, never
// covers it) — meant to sit for a while without nagging (Jason 2026-08-23:
// "persist until deleted... not rely on users hitting their notification
// inbox"). Read is public (RLS: announcements_select_all); writes go through
// is_admin()-gated admin_create_announcement / admin_delete_announcement, same
// shape as ops_alerts' admin_* RPCs.
//
// One concession to humaneness: a ✕ hides a given announcement on THIS device
// only (localStorage, by id) — otherwise a real "sits for days" notice would
// nag the same person every single reload. It stays live for everyone else and
// reappears if they clear storage or use another device; only an admin
// deleting it removes it everywhere. That's the "persist until deleted"
// contract — the ✕ is a local mute, not the delete.
const SEEN_KEY = 'nextep_announce_dismissed'
// First time this browser ever rendered the banner. Notices published BEFORE that moment
// are history to this person — a brand-new guest was greeted with "New Themes" about a
// picker they'd never seen (2026-09-08). Old notices stay available to old devices.
const SINCE_KEY = 'nextep_announce_since'
function getSince() {
  try {
    const v = safeStorage.get(SINCE_KEY)
    if (v) return v
    const now = new Date().toISOString()
    safeStorage.set(SINCE_KEY, now)
    return now
  } catch { return null }
}
function getDismissed() {
  try { return new Set(JSON.parse(safeStorage.get(SEEN_KEY) || '[]')) } catch { return new Set() }
}
function addDismissed(id) {
  try {
    const s = getDismissed(); s.add(id)
    safeStorage.set(SEEN_KEY, JSON.stringify([...s].slice(-50)))   // bounded — never grows forever
  } catch {}
}

const VARIANTS = {
  error:   { Icon: AlertOctagon,  bg: 'linear-gradient(180deg, #5c1414, #430e0e)', border: 'rgba(231,76,60,0.45)',  iconColor: '#ff8b7f', label: 'Notice' },
  warning: { Icon: AlertTriangle, bg: 'linear-gradient(180deg, #5e440d, #47330a)', border: 'rgba(245,200,66,0.4)',  iconColor: '#ffcf6b', label: 'Heads up' },
  info:    { Icon: Info,          bg: 'linear-gradient(180deg, #12305e, #0c2247)', border: 'rgba(91,156,246,0.4)',  iconColor: '#7fb3ff', label: 'Notice' },
  success: { Icon: CheckCircle2,  bg: 'linear-gradient(180deg, #11401f, #0b2f16)', border: 'rgba(46,204,113,0.4)',  iconColor: '#6fe39a', label: 'Good news' },
}

export function AnnouncementBanner() {
  const [rows, setRows] = useState([])
  const [dismissed, setDismissed] = useState(getDismissed)
  const [leavingIds, setLeavingIds] = useState(new Set())

  const load = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('announcements')
        .select('id, severity, title, body, created_at').order('created_at', { ascending: false })
      if (!error && Array.isArray(data)) setRows(data)
    } catch { /* offline — keep whatever we had */ }
  }, [])

  useEffect(() => {
    // Rare broadcast: first read waits for idle, re-reads every 10 min while visible only.
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500))
    const first = ric(load)
    const t = setInterval(() => { if (document.visibilityState === 'visible') load() }, 10 * 60000)
    return () => { clearInterval(t); (window.cancelIdleCallback || clearTimeout)(first) }
  }, [load])

  const dismiss = (id) => {
    setLeavingIds(prev => new Set(prev).add(id))
    setTimeout(() => {
      addDismissed(id)
      setDismissed(getDismissed())
      setLeavingIds(prev => { const n = new Set(prev); n.delete(id); return n })
    }, 220)
  }

  const since = getSince()
  const visible = rows.filter(r => !dismissed.has(r.id) && (!since || !r.created_at || r.created_at >= since))
  if (!visible.length) return null

  return (
    <div style={{ fontFamily: 'var(--font)' }}>
      <style>{`
        @keyframes announceIn { from { opacity:0; transform:translateY(-8px) } to { opacity:1; transform:translateY(0) } }
        @keyframes announceLeave { from { opacity:1; max-height:120px } to { opacity:0; max-height:0; padding-top:0; padding-bottom:0; margin-bottom:0 } }
      `}</style>
      {visible.map(a => {
        const v = VARIANTS[a.severity] || VARIANTS.info
        const Icon = v.Icon
        return (
          <div key={a.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: 11,
            padding: '11px 16px', paddingTop: 'max(11px, env(safe-area-inset-top))',
            background: v.bg, borderBottom: `1px solid ${v.border}`,
            boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
            '--nxa': leavingIds.has(a.id) ? 'announceLeave 220ms ease-out forwards' : 'announceIn 0.3s ease',
            overflow: 'hidden',
          }}>
            <Icon size={18} color={v.iconColor} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}>{a.title}</div>
              {a.body && <div style={{ color: 'rgba(var(--ink-rgb),0.8)', fontSize: 12, lineHeight: 1.5, marginTop: 2 }}>{a.body}</div>}
            </div>
            <button onClick={() => dismiss(a.id)} aria-label="Dismiss this notice" style={{
              flexShrink: 0, width: 30, height: 30, borderRadius: '50%', border: '1px solid rgba(var(--ink-rgb),0.22)',
              background: 'rgba(var(--ink-rgb),0.08)', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: -1,
            }}>
              <X size={13} />
            </button>
          </div>
        )
      })}
    </div>
  )
}
